// app/_layout.tsx
import "react-native-gesture-handler";
import "@/lib/webPolyfills";
import { PushNotificationsBridge } from "@/components/PushNotificationsBridge";
import { UpdateModal } from "@/components/UpdateModal";
import { TopToastProvider } from "@/components/ui/TopToastProvider";
import { useAppVersion } from "@/hooks/useAppVersion";
import * as Sentry from "@sentry/react-native";
import { PortalHost, PortalProvider } from "@tamagui/portal";
import { QueryClientProvider } from "@tanstack/react-query";
import Constants from "expo-constants";
import { useFonts } from "expo-font";
import { Stack, useNavigationContainerRef } from "expo-router";
import Head from "expo-router/head";
import React, { useEffect } from "react";
import { ActivityIndicator, Platform, StatusBar, View, useColorScheme } from "react-native";
import { LOADING_SPINNER_COLOR } from "@/lib/loadingSpinnerColor";
import { SENTRY_IGNORE_ERRORS, SENTRY_WEB_ALLOW_URLS, sentryBeforeSend } from "@/lib/sentryFilters";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { TamaguiProvider, Theme } from "tamagui";
import { AppErrorBoundary } from "../components/ui/AppErrorBoundary";
import { ErrorHandlerProvider } from "../components/ui/ErrorHandlerProvider";
import { AuthProvider } from "../lib/AuthContext";
import { MixpanelSubscriptionTraitsSync } from "@/components/analytics/MixpanelSubscriptionTraitsSync";
import { SSOTokenProvider } from "../hooks/useSSOTokenExchange";
import { logger } from "../lib/logger";
import { queryClient } from "../lib/queryClient";
import { supabase } from "../lib/supabase";
import { fathomService } from "../services/fathom.service";
import { gaService } from "../services/ga.service";
import { mixpanelService } from "../services/mixpanel.service";
import { config } from "../tamagui.config";

// Helper to check if running in Expo Go
function isRunningInExpoGo(): boolean {
  return Constants.executionEnvironment === "storeClient";
}

// Get environment and release info
const environment = Constants.expoConfig?.extra?.appVariant || 'development';

// Check if Sentry should be initialized
// Don't initialize in local development (__DEV__ = true)
const shouldInitializeSentry = !__DEV__;

// Determine if we're in production (not staging)
const isProduction = environment === 'production';

// Initialize Sentry navigation integration only if Sentry will be initialized
const navigationIntegration = shouldInitializeSentry 
  ? Sentry.reactNavigationIntegration({
      enableTimeToInitialDisplay: !isRunningInExpoGo(),
    })
  : null;

// For web builds, use EXPO_PUBLIC_RELEASE_SHA (set during Vercel build from VERCEL_GIT_COMMIT_SHA)
// This ensures the release matches the source maps uploaded during build
const release = Platform.OS === 'web'
  ? (process.env.EXPO_PUBLIC_RELEASE_SHA || process.env.VERCEL_GIT_COMMIT_SHA)
  : undefined;

const dist = Platform.OS === 'web' 
  ? (process.env.EXPO_PUBLIC_RELEASE_SHA || process.env.VERCEL_GIT_COMMIT_SHA)
  : undefined;

if (shouldInitializeSentry) {
  const tracesSampleRate = isProduction ? 0.1 : 1.0;
  const replaysSessionSampleRate = isProduction ? 0.01 : 0.1;
  
  Sentry.init({
    dsn: "https://ebf19ae57d8e62c08ba966909eefbf81@o4510262831153153.ingest.us.sentry.io/4510262832660480",
    tracesSampleRate: tracesSampleRate,
    release: release,
    dist: dist,
    sendDefaultPii: true,
    enableLogs: true,
    environment: environment,
    replaysSessionSampleRate: replaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      navigationIntegration!,
      Sentry.mobileReplayIntegration(),
    ],
    enableNativeFramesTracking: !isRunningInExpoGo(),
    // Noise controls (see lib/sentryFilters.ts). allowUrls is web-only: on
    // native every frame lives in main.jsbundle and would be filtered out.
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    ...(Platform.OS === 'web' ? { allowUrls: SENTRY_WEB_ALLOW_URLS } : {}),
    beforeSend: sentryBeforeSend,
  });
} else {
  __DEV__ && logger.debug('Sentry disabled in local development');
}

function RootLayout() {
  const scheme = useColorScheme();
  const themeName = scheme === "light" ? "light" : "dark";
  const ref = useNavigationContainerRef();

  // Version checking hook (polls every 10 min, reloads on navigation if update available)
  const { showStoreUpdateModal, updateInfo, dismissStoreUpdate } = useAppVersion();

  const [loaded] = useFonts({
    InterRegular: require("../assets/fonts/Inter-Regular.ttf"),
    InterMedium: require("../assets/fonts/Inter-Medium.ttf"),
    InterSemiBold: require("../assets/fonts/Inter-SemiBold.ttf"),
    InterBold: require("../assets/fonts/Inter-Bold.ttf"),
    InterItalic: require("../assets/fonts/Inter-Italic.ttf"),
    InterMediumItalic: require("../assets/fonts/Inter-MediumItalic.ttf"),
    InterSemiBoldItalic: require("../assets/fonts/Inter-SemiBoldItalic.ttf"),
    InterBoldItalic: require("../assets/fonts/Inter-BoldItalic.ttf"),
  });

  // Register navigation container with Sentry
  useEffect(() => {
    if (ref && navigationIntegration) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  // Track app opened event only if user is logged in
  useEffect(() => {
      
      const setupMixpanelAndTrack = async () => {
          // Guaranteed Mixpanel initialization
          await mixpanelService.initialize();

          // Initialize Google Analytics (web only)
          if (Platform.OS === "web") {
            gaService.initialize();
            fathomService.initialize();
          }

          // Get Supabase User (Anonymous or Signed In)
          const {
              data: { session },
          } = await supabase.auth.getSession();
          
          const user = session?.user || null;
          // console.log('user', user)
          
          // IDENTIFY IF EMAIL EXISTS (RESUME SESSION)
          if (user?.email) {
              // Call Mixpanel identity to resume the session with email
              await mixpanelService.identifyUser(user.email);

              // Set GA user properties
              if (Platform.OS === 'web') {
                gaService.setUserProperties(user);
              }
          }

          // One "Opened App" per cold start, after identify so it attributes
          // to the resumed user (anonymous sessions still count).
          await mixpanelService.trackAppOpened(user);
    };

    setupMixpanelAndTrack().catch((err) => {
      logger.error('setupMixpanelAndTrack failed:', err);
    });
}, []);

  if (!loaded) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff' }}>
        <ActivityIndicator size="large" color={LOADING_SPINNER_COLOR} />
      </View>
    );
  }

  return (
    <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
            <MixpanelSubscriptionTraitsSync />
            <GestureHandlerRootView style={{ flex: 1 }}>
              <SafeAreaProvider>
              {/* Mobile Store Update Modal */}
              {Platform.OS !== 'web' && showStoreUpdateModal && updateInfo && (
                <UpdateModal
                  visible={showStoreUpdateModal}
                  isMandatory={updateInfo.isMandatory}
                  version={updateInfo.latestVersion}
                  releaseNotes={updateInfo.releaseNotes}
                  onDismiss={dismissStoreUpdate}
                />
              )}
              {Platform.OS === "web" && (
                <Head>
                  <link rel="preconnect" href="https://fonts.googleapis.com" />
                  <link
                    rel="preconnect"
                    href="https://fonts.gstatic.com"
                    crossOrigin=""
                  />
                  <link
                    href="https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap"
                    rel="stylesheet"
                  />
                  <style>{`html,body,#root{font-synthesis-weight:none;font-synthesis-style:none;}`}</style>
                </Head>
              )}

              <StatusBar
                translucent={false}
                barStyle={"light-content"}
                backgroundColor={themeName === "dark" ? "#000" : "#fff"}
              />

              <TamaguiProvider config={config}>
                <Theme name="light">
                  <PortalProvider>
                    <TopToastProvider>
                      <ErrorHandlerProvider>
                        <SSOTokenProvider>
                          <PushNotificationsBridge />
                          <PortalHost name="root" />
                          <Stack
                            screenOptions={{
                              headerShown: false,
                              presentation: "card",
                            }}
                          >
                            <Stack.Screen name="(main)" />
                            <Stack.Screen name="auth" />
                            <Stack.Screen name="+not-found" />
                          </Stack>
                        </SSOTokenProvider>
                      </ErrorHandlerProvider>
                    </TopToastProvider>
                  </PortalProvider>
                </Theme>
              </TamaguiProvider>
              </SafeAreaProvider>
            </GestureHandlerRootView>
      </AuthProvider>
    </QueryClientProvider>
    </AppErrorBoundary>
  );
}

// Only wrap with Sentry if it was initialized
export default shouldInitializeSentry ? Sentry.wrap(RootLayout) : RootLayout;